/**
 * GenerationSpec — the immutable request value object every generation path
 * (UI, Canvas, Supercomputer, MCP, CLI, plugins, apps) submits to the ONE
 * control plane (ADR-007). Params are validated against the model manifest
 * at the boundary; invalid combos return 422 with JSON-pointer paths.
 */
import { z } from 'zod';
export const SURFACES = ['web', 'mcp', 'cli', 'canvas', 'supercomputer', 'plugin', 'app'];
export const ROUTE_MODES = ['explicit', 'best', 'fast', 'economy'];
export const generationParamsSchema = z
    .object({
    prompt: z.string().max(4000).optional(),
    negativePrompt: z.string().max(2000).optional(),
    mode: z.enum(['t2i', 'i2i', 't2v', 'i2v', 't2a', 'v2v']).optional(),
    startFrameAssetId: z.string().uuid().optional(),
    endFrameAssetId: z.string().uuid().optional(),
    /** Painted inpainting mask (white = edit region), stored as an asset. */
    maskAssetId: z.string().uuid().optional(),
    /** Driving audio for lipsync/talking-avatar engines. */
    audioAssetId: z.string().uuid().optional(),
    durationSec: z.number().positive().optional(),
    resolution: z.string().optional(),
    aspect: z.string().optional(),
    audio: z.boolean().optional(),
    referenceAssetIds: z.array(z.string().uuid()).max(8).optional(),
    batch: z.number().int().min(1).max(8).optional(),
    style: z.string().optional(),
    voice: z.string().optional(),
    text: z.string().max(5000).optional(),
    /** Spokesperson pipeline: scene direction for the presenter frame that
     * gets rendered (with the product, when a start frame is given) before
     * the audio-driven performance step. */
    presenterPrompt: z.string().max(2500).optional(),
})
    .strict();
export const generationSpecSchema = z
    .object({
    /** Explicit model, or omitted when routeMode is best/fast/economy. */
    modelId: z.string().optional(),
    routeMode: z.enum(ROUTE_MODES).default('explicit'),
    modality: z.enum(['image', 'video', 'audio']),
    params: generationParamsSchema,
    /** BCP-47-ish locale driving cultural packs + TTS routing (ADR-010). */
    locale: z.string().default('en'),
    cultural: z.string().optional(),
})
    .strict()
    .refine((s) => s.routeMode !== 'explicit' || !!s.modelId, {
    message: 'modelId is required when routeMode is "explicit"',
    path: ['modelId'],
});
/**
 * Validate spec params against a manifest's declared capabilities.
 * This is the "unsupported combos are impossible" gate — shared by the API
 * (422 responses) and the FE (disable controls before submit).
 */
export function validateSpecAgainstManifest(spec, manifest) {
    const issues = [];
    const caps = manifest.capabilities;
    const p = spec.params;
    if (spec.modality !== manifest.modality) {
        issues.push({ pointer: '/modality', message: `model ${manifest.id} is ${manifest.modality}, not ${spec.modality}` });
    }
    if (p.mode && !caps.modes.includes(p.mode)) {
        issues.push({ pointer: '/params/mode', message: `mode "${p.mode}" not supported; supported: ${caps.modes.join(', ')}` });
    }
    if (p.startFrameAssetId && !caps.startFrame) {
        issues.push({ pointer: '/params/startFrameAssetId', message: 'start frame not supported by this model' });
    }
    if (p.endFrameAssetId && !caps.endFrame) {
        issues.push({ pointer: '/params/endFrameAssetId', message: 'end frame not supported by this model' });
    }
    if (p.maskAssetId && !caps.mask) {
        issues.push({ pointer: '/params/maskAssetId', message: 'mask inpainting not supported by this model' });
    }
    if (p.audioAssetId && !caps.audioDrive) {
        issues.push({ pointer: '/params/audioAssetId', message: 'driving audio not supported by this model' });
    }
    if (spec.modality === 'video' && p.text && !caps.audioDrive) {
        issues.push({ pointer: '/params/text', message: 'speech script requires an audio-driven avatar model' });
    }
    if (p.presenterPrompt && !caps.audioDrive) {
        issues.push({ pointer: '/params/presenterPrompt', message: 'presenter staging requires an audio-driven avatar model' });
    }
    if (p.durationSec !== undefined && caps.durationsSec && !caps.durationsSec.includes(p.durationSec)) {
        issues.push({
            pointer: '/params/durationSec',
            message: `duration must be one of: ${caps.durationsSec.join(', ')}s`,
        });
    }
    if (p.resolution && caps.resolutions && !caps.resolutions.includes(p.resolution)) {
        issues.push({ pointer: '/params/resolution', message: `resolution must be one of: ${caps.resolutions.join(', ')}` });
    }
    if (p.aspect && caps.aspects && !caps.aspects.includes(p.aspect)) {
        issues.push({ pointer: '/params/aspect', message: `aspect must be one of: ${caps.aspects.join(', ')}` });
    }
    if (p.audio && !caps.nativeAudio) {
        issues.push({ pointer: '/params/audio', message: 'native audio not supported by this model' });
    }
    if (p.referenceAssetIds?.length && !caps.referenceImages) {
        issues.push({ pointer: '/params/referenceAssetIds', message: 'reference images not supported by this model' });
    }
    else if (p.referenceAssetIds && caps.referenceImages && p.referenceAssetIds.length > caps.referenceImages.max) {
        issues.push({
            pointer: '/params/referenceAssetIds',
            message: `at most ${caps.referenceImages.max} reference images`,
        });
    }
    if (p.batch !== undefined && p.batch > (caps.maxBatch ?? 1)) {
        issues.push({ pointer: '/params/batch', message: `batch limited to ${caps.maxBatch ?? 1} for this model` });
    }
    if (!p.prompt && manifest.ui.controls.includes('prompt')) {
        issues.push({ pointer: '/params/prompt', message: 'prompt is required' });
    }
    return issues;
}
/** Job lifecycle states — the state machine is enforced in the domain layer. */
export const JOB_STATES = [
    'QUEUED',
    'MODERATING',
    'ROUTING',
    'GENERATING',
    'POST_PROCESSING',
    'SUCCEEDED',
    'FAILED',
    'CANCELLED',
];
/**
 * Legal state transitions; anything else throws in JobService.
 * Every non-terminal state may re-enter MODERATING: queue redelivery after a
 * mid-flight crash restarts the pipeline from the top (idempotent executors
 * make that safe) — without this, any retry after reaching GENERATING would
 * dead-end on an illegal transition.
 */
export const JOB_TRANSITIONS = {
    QUEUED: ['MODERATING', 'CANCELLED', 'FAILED'],
    MODERATING: ['MODERATING', 'ROUTING', 'FAILED', 'CANCELLED'],
    ROUTING: ['MODERATING', 'GENERATING', 'FAILED', 'CANCELLED'],
    GENERATING: ['MODERATING', 'POST_PROCESSING', 'FAILED', 'CANCELLED'],
    POST_PROCESSING: ['MODERATING', 'SUCCEEDED', 'FAILED'],
    SUCCEEDED: [],
    FAILED: [],
    CANCELLED: [],
};
//# sourceMappingURL=spec.js.map